import { useFocusHeading } from '@windrop/ui';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

export interface Crumb {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  breadcrumb?: Crumb[];
  /** Primary and secondary actions (Buttons / links). */
  actions?: ReactNode;
  /** Badge or status next to the title. */
  meta?: ReactNode;
}

/** Page header with breadcrumb and actions. The h1 receives focus on route change so screen readers land on it. */
export function PageHeader({ title, description, breadcrumb, actions, meta }: PageHeaderProps) {
  const headingRef = useFocusHeading<HTMLHeadingElement>(typeof title === 'string' ? title : undefined);
  return (
    <header className="wdc-page-header">
      {breadcrumb && breadcrumb.length ? (
        <nav aria-label="Breadcrumb" className="wdc-breadcrumb">
          <ol>
            {breadcrumb.map((c, i) => (
              <li key={`${c.label}-${i}`} aria-current={i === breadcrumb.length - 1 ? 'page' : undefined}>
                {c.to && i < breadcrumb.length - 1 ? (
                  <Link to={c.to}>{c.label}</Link>
                ) : (
                  <span>{c.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}
      <div className="wdc-page-header__row">
        <div className="wdc-page-header__title">
          <h1 ref={headingRef} tabIndex={-1} className="wd-display-sm">
            {title}
          </h1>
          {meta}
        </div>
        {actions ? <div className="wd-btn-group wdc-page-header__actions">{actions}</div> : null}
      </div>
      {description ? <p className="wd-muted wdc-page-header__desc">{description}</p> : null}
    </header>
  );
}
